import React, { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../apiClient';
import { useAuth } from '../AuthContext';
import { isPharmacyUser } from '../utils/businessType';
import './PharmacyExpiryPage.css';

export default function PharmacyExpiryPage({ onNavigate }) {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [actionId, setActionId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const data = await apiJson('/pharmacy/expiry-report');
      setReport(data);
    } catch (e) {
      setReport(null);
      setErr(e.message || 'Could not load expiry report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPharmacyUser(user)) return;
    void load();
  }, [user, load]);

  const writeOff = async (line) => {
    if (!line.batchId && !window.confirm(`Write off all ${line.quantity} units of ${line.productName}?`)) {
      return;
    }
    const batchId = line.batchId;
    if (!batchId) {
      window.alert('Open product list to manage batches without batch id.');
      return;
    }
    if (!window.confirm(`Write off ${line.quantity} expired units of ${line.productName}?`)) return;
    setActionId(batchId);
    try {
      await apiJson(`/pharmacy/batches/${batchId}/write-off`, {
        method: 'POST',
        body: JSON.stringify({ quantity: line.quantity, reason: 'Expired — pharmacy write-off' }),
      });
      await load();
    } catch (e) {
      window.alert(e.message || 'Write-off failed.');
    } finally {
      setActionId(null);
    }
  };

  if (!isPharmacyUser(user)) {
    return (
      <div className="pharm-expiry page-bleed">
        <p>Expiry &amp; batch tools are for pharmacy accounts only.</p>
      </div>
    );
  }

  return (
    <div className="pharm-expiry page-bleed">
      <header className="pharm-expiry__head">
        <div>
          <h1>Expiry &amp; batches (TMDA-style register)</h1>
          <p className="pharm-expiry__lede">
            Track expired and near-expiry stock. POS uses <strong>FEFO</strong> (oldest expiry sold first).
            Write off expired units here so they are not sold.
          </p>
        </div>
        <button type="button" className="pharm-expiry__btn" onClick={() => onNavigate?.('stock-add')}>
          + Receive stock batch
        </button>
      </header>

      {loading ? <p className="pharm-expiry__muted">Loading…</p> : null}
      {err ? <p className="pharm-expiry__err">{err}</p> : null}

      {report ? (
        <>
          <div className="pharm-expiry__stats">
            <div className="pharm-expiry__stat pharm-expiry__stat--danger">
              <span className="pharm-expiry__stat-num">{report.expiredUnits ?? 0}</span>
              <span className="pharm-expiry__stat-label">Expired units in stock</span>
            </div>
            <div className="pharm-expiry__stat pharm-expiry__stat--warn">
              <span className="pharm-expiry__stat-num">{report.soonUnits ?? 0}</span>
              <span className="pharm-expiry__stat-label">
                Expiring within {report.warningDays ?? 30} days
              </span>
            </div>
          </div>

          <div className="pharm-expiry__table-wrap">
            <table className="pharm-expiry__table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Batch</th>
                  <th>Expiry</th>
                  <th>Qty</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(report.lines || []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="pharm-expiry__empty">
                      No expired or near-expiry batches. Good stock hygiene.
                    </td>
                  </tr>
                ) : (
                  (report.lines || []).map((line, idx) => (
                    <tr key={`${line.productId}-${line.expiryDate}-${idx}`}>
                      <td>
                        <strong>{line.productName}</strong>
                        {line.barcode ? (
                          <span className="pharm-expiry__barcode">{line.barcode}</span>
                        ) : null}
                      </td>
                      <td>{line.category || '—'}</td>
                      <td>{line.batchLot || '—'}</td>
                      <td>{line.expiryDate || '—'}</td>
                      <td>{line.quantity}</td>
                      <td>
                        <span className={`pharm-expiry__pill pharm-expiry__pill--${line.expiryStatus}`}>
                          {line.expiryStatus === 'expired' ? 'Expired' : 'Expiring soon'}
                        </span>
                      </td>
                      <td>
                        {line.expiryStatus === 'expired' ? (
                          <button
                            type="button"
                            className="pharm-expiry__btn pharm-expiry__btn--small"
                            disabled={String(actionId) === String(line.batchId)}
                            onClick={() => writeOff({ ...line, batchId: line.batchId })}
                          >
                            Write off
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="pharm-expiry__btn pharm-expiry__btn--ghost pharm-expiry__btn--small"
                            onClick={() => onNavigate?.('stock-list')}
                          >
                            View stock
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="pharm-expiry__foot">
            Tip: scan barcode at POS to sell. System blocks sales when only expired batches remain.
          </p>
        </>
      ) : null}
    </div>
  );
}
